/**
 * The Funnel page's six headline figures: what each one is, where it comes from
 * in `GET /overview/funnel`, and how a stage with no events or a zero
 * denominator reads. Every value is the API's own figure for a stage; nothing
 * here recomputes a rate from counts.
 */

import type { FunnelStage, FunnelStageFigures, FunnelStageKey } from '@/api/types'
import { FUNNEL_STAGE_LABELS } from '@/api/types'
import { percentChange, type Polarity } from '@/design/format'

import { isMeasured, measuredBefore, negativeDropOffReason, noEventsReason } from './figures'

export const FUNNEL_KPI_KEYS = [
  'website_visitors',
  'add_to_basket_rate',
  'checkout_conversion',
  'purchase_conversion',
  'basket_abandonment',
  'payment_failure_rate',
] as const
export type FunnelKpiKey = (typeof FUNNEL_KPI_KEYS)[number]

/** The stage figures a headline reads: a count, or one of the API's rates. */
type FigureKey = Extract<keyof FunnelStageFigures, 'sessions' | 'totalConversionRate' | 'dropOffRate'>

export interface FunnelKpiDefinition {
  key: FunnelKpiKey
  label: string
  /** The stage whose figure this is. */
  stage: FunnelStageKey
  figure: FigureKey
  /** The stage the rate is "of": the denominator, named when it is zero. */
  base: FunnelStageKey | null
  format: 'integer' | 'percent'
  polarity: Polarity
  /** A few words under the figure. */
  summary: string
  /** The full definition, in the page's "How these are measured". */
  definition: string
}

export const FUNNEL_KPIS: Record<FunnelKpiKey, FunnelKpiDefinition> = {
  website_visitors: {
    key: 'website_visitors',
    label: 'Website Visitors',
    stage: 'visitors',
    figure: 'sessions',
    base: null,
    format: 'integer',
    polarity: 'up_good',
    summary: 'Measured sessions',
    definition:
      'Measured sessions that started in the period: browser sessions that sent at least one behavioural event, which the site sends only for visitors who allowed analytics. Sessions, not people.',
  },
  add_to_basket_rate: {
    key: 'add_to_basket_rate',
    label: 'Add to Basket Rate',
    stage: 'add_to_basket',
    figure: 'totalConversionRate',
    base: 'visitors',
    format: 'percent',
    polarity: 'up_good',
    summary: 'Sessions adding to basket ÷ visitors',
    definition: 'Measured sessions that added a ticket to the basket, divided by all measured sessions.',
  },
  checkout_conversion: {
    key: 'checkout_conversion',
    label: 'Checkout Conversion',
    stage: 'checkout_started',
    figure: 'totalConversionRate',
    base: 'visitors',
    format: 'percent',
    polarity: 'up_good',
    summary: 'Sessions starting checkout ÷ visitors',
    definition: 'Measured sessions that started checkout, divided by all measured sessions.',
  },
  purchase_conversion: {
    key: 'purchase_conversion',
    label: 'Purchase Conversion',
    stage: 'purchase_completed',
    figure: 'totalConversionRate',
    base: 'visitors',
    format: 'percent',
    polarity: 'up_good',
    summary: 'Sessions purchasing ÷ visitors',
    definition: 'Measured sessions that completed a purchase, divided by all measured sessions.',
  },
  basket_abandonment: {
    key: 'basket_abandonment',
    label: 'Basket Abandonment',
    stage: 'checkout_started',
    figure: 'dropOffRate',
    base: 'add_to_basket',
    format: 'percent',
    polarity: 'down_good',
    summary: 'Basket sessions not starting checkout',
    definition:
      'Of the measured sessions that added to the basket, the share that did not start checkout: (add to basket − checkout started) ÷ add to basket.',
  },
  payment_failure_rate: {
    key: 'payment_failure_rate',
    label: 'Payment Failure Rate',
    stage: 'purchase_completed',
    figure: 'dropOffRate',
    base: 'payment_attempted',
    format: 'percent',
    polarity: 'down_good',
    summary: 'Payment attempts not completing',
    definition:
      'Of the measured sessions that attempted payment, the share that did not complete a purchase: (payment attempted − purchase completed) ÷ payment attempted. It includes declined payments and payments left unfinished.',
  },
}

/**
 * A headline as the page shows it. `measured` is false when a stage it reads
 * had no events in the period ("Not measured"); `value` is null when the
 * figure has no denominator ("—"). Either way `reason` says why.
 */
export interface FunnelKpiReading {
  definition: FunnelKpiDefinition
  measured: boolean
  value: number | null
  /** The comparison period's figure; null with comparison off, or when it is not there. */
  previous: number | null
  /** Relative change against the previous period's figure; null without one. */
  change: number | null
  reason: string | null
  /** Why there is no change although the period is compared: a stage it reads had no events then, or a drop-off is negative. */
  previousReason: string | null
  /** A figure that reads oddly but is right: a negative drop-off, when more sessions reached a stage than the one before. */
  caveat: string | null
}

function stageLabel(key: FunnelStageKey): string {
  return FUNNEL_STAGE_LABELS[key].toLowerCase()
}

/** The stages a headline depends on: the one its rate divides by, then its own. */
export function kpiStages(definition: FunnelKpiDefinition): FunnelStageKey[] {
  return definition.base ? [definition.base, definition.stage] : [definition.stage]
}

/** A headline with no figure: a stage it reads had no events in the period. */
function unmeasuredReading(definition: FunnelKpiDefinition, missing: FunnelStageKey): FunnelKpiReading {
  return {
    definition,
    measured: false,
    value: null,
    previous: null,
    change: null,
    reason: noEventsReason(missing, 'this'),
    previousReason: null,
    caveat: null,
  }
}

/**
 * The previous period's figure and, when it has none although the period is
 * compared, why: by the same rule as this one, a stage with no events then is
 * not measured, never a change from 0 % or 100 %.
 */
function previousPeriod(
  definition: FunnelKpiDefinition,
  stage: FunnelStage,
  used: readonly FunnelStage[],
): { figure: number | null; reason: string | null } {
  if (!stage.previous) return { figure: null, reason: null }
  if (!measuredBefore(used)) return { figure: null, reason: 'Not measured in the previous period' }
  return { figure: stage.previous[definition.figure], reason: null }
}

/**
 * A relative change only between two figures that are not negative. A drop-off
 * rate is negative when more sessions reached a stage than the one before; a
 * change from or to one has no meaning, and could colour a rise in
 * abandonment as good news.
 */
function changeBetween(value: number | null, previous: number | null): number | null {
  if (value === null || previous === null || value < 0 || previous < 0) return null
  return percentChange(value, previous)
}

/** Why a compared headline still has no change: a negative drop-off now or then. */
function negativeReason(value: number | null, previous: number | null): string | null {
  if (previous !== null && previous < 0) return 'No comparison: the previous period’s drop-off was negative'
  if (previous !== null && value !== null && value < 0) return 'No comparison for a negative drop-off'
  return null
}

/** A negative drop-off is the API's own figure, not an error: more sessions reached the stage than the one before. */
function caveatFor(definition: FunnelKpiDefinition, value: number | null): string | null {
  if (definition.figure !== 'dropOffRate' || !definition.base || value === null || value >= 0) return null
  return negativeDropOffReason(definition.stage, definition.base)
}

export function readFunnelKpi(definition: FunnelKpiDefinition, stages: readonly FunnelStage[]): FunnelKpiReading {
  const byKey = new Map(stages.map((stage) => [stage.key, stage]))
  // A stage the answer lacks is as unmeasured as one it lists without events.
  const base = definition.base ? byKey.get(definition.base) : undefined
  if (definition.base && (!base || !isMeasured(base))) return unmeasuredReading(definition, definition.base)
  const stage = byKey.get(definition.stage)
  if (!stage || !isMeasured(stage)) return unmeasuredReading(definition, definition.stage)
  const used = base ? [base, stage] : [stage]
  const value = stage[definition.figure]
  const { figure: previous, reason: previousReason } = previousPeriod(definition, stage, used)
  return {
    definition,
    measured: true,
    value,
    previous,
    change: changeBetween(value, previous),
    reason:
      value === null && definition.base ? `No ${stageLabel(definition.base)} sessions in this period to divide by` : null,
    previousReason: previousReason ?? negativeReason(value, previous),
    caveat: caveatFor(definition, value),
  }
}

export function readFunnelKpis(stages: readonly FunnelStage[]): FunnelKpiReading[] {
  return FUNNEL_KPI_KEYS.map((key) => readFunnelKpi(FUNNEL_KPIS[key], stages))
}
